import React, { useEffect, useState } from 'react'
import buttons from '../../../images/icon_button.png'
import joysticks from '../../../images/icon_joystick.png'
import hybrid_hard from '../../../images/icon_hybrid_hard.png'
import touch from '../../../images/icon_touch_hard.png'

import { Box, Slider } from '@mui/material'
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen'
import { usePostMqttChangeVolume } from '../../../mqtt/mqttHandlerContext'
import { InstrumentType, SONG_TYPES, Song, Instrument, TouchMode } from '../../../utils/types'
import { Drawer } from '../Welcome'
import Text from '../../../assets/components/Text'
import { useCurrentUser } from '../../../hooks/contexts/currentUserContext'
import Funkingen from '../../../assets/icons/funkingen_2.png'
import InstrumentInformation from './Components/InstrumentInformation'
import { VolumeIcon } from '../../../components/DynamicVolumeIcon'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import AutorenewIcon from '@mui/icons-material/Autorenew'

import { useContext } from 'react'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'
import AllSoundsAvailable from '../Components/AllSoundsAvailable'
import { isMobile } from 'mobile-device-detect'

export const VOLUME_MIN = 0
export const VOLUME_MAX = 0.3
export const VOLUME_STEPS = 7 // 7 positions including min & max
export const VOLUME_STEP = (VOLUME_MAX - VOLUME_MIN) / (VOLUME_STEPS - 1)
export const VOLUME_MARKS = Array.from({ length: VOLUME_STEPS }, (_, i) => ({
    value: parseFloat((VOLUME_MIN + i * VOLUME_STEP).toFixed(3))
}))

// Returnerar korrekt bild beroende på vilket instrument id vi anropar med
function handleImageSource(type: InstrumentType, touchMode?: TouchMode): string {
    switch (type) {
        case 'buttons':
            return buttons
        case 'touch':
            return touch
        case 'joysticks':
            return joysticks
        case 'hybrid':
            return hybrid_hard
        default:
            return buttons
    }
}

interface InstrumentsProps {
    currentSong: Song | undefined
    instrumentVolumes: { [key: string]: number }
    setInstrumentVolumes: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
    connectedInstruments: Instrument[]
}

const Instruments = ({ currentSong, instrumentVolumes, setInstrumentVolumes, connectedInstruments }: InstrumentsProps) => {
    const { polyglot } = useContext(PolyglotContext)

    // const [isOpen, setIsOpen] = useState(false);
    const [selectedInstrument, setSelectedInstrument] = useState<Instrument>({
        deviceId: '',
        type: '',
        serialNr: '',
        color: ''
    })
    const [isAllSongsDrawerOpen, setIsAllSongsDrawerOpen] = useState(false)

    const postMqttChangeVolume = usePostMqttChangeVolume()
    const isSmallScreen = useIsSmallScreen()

    const currentUser = useCurrentUser()
    const songList = currentUser?.songList
    const shouldShowSettings = currentSong?.type === SONG_TYPES.KEY

    const toggleAllSongsDrawer = ({ instrumentId, instrumentType, instrumentSerialNr, color }: { instrumentId?: string; instrumentType?: InstrumentType; instrumentSerialNr?: string; color?: string }) => {
        setIsAllSongsDrawerOpen(!isAllSongsDrawerOpen)
        setSelectedInstrument({
            color: color ?? '',
            deviceId: instrumentId ?? '',
            type: instrumentType ?? '',
            serialNr: instrumentSerialNr ?? ''
        })
    }

    //Close drawer after user has clicked a new sound
    const hasChangedSoundCloseDrawer = () => {
        setTimeout(() => {
            setIsAllSongsDrawerOpen(false)
        }, 300)
    }

    // send only when the user releases the thumb (mouse up / touch end)
    const handleSliderCommit = (_: Event | React.SyntheticEvent, value: number | number[], instrumentId: string) => {
        const vol = Array.isArray(value) ? value[0] : value
        // persist local UI state (already set in onChange, so optional)
        setInstrumentVolumes((prev) => ({ ...prev, [instrumentId]: vol }))
        // one MQTT message per commit
        postMqttChangeVolume(vol.toFixed(3), instrumentId)
    }

    // update UI state while dragging (no MQTT)
    const handleSliderChange = (_: Event | React.SyntheticEvent, value: number | number[], instrumentId: string) => {
        const vol = Array.isArray(value) ? value[0] : value
        setInstrumentVolumes((prev) => ({ ...prev, [instrumentId]: vol }))
    }

    // Fetch initial volume values from storage when the component mounts
    useEffect(() => {
        const storedVolumesString = localStorage.getItem('instrumentVolumes')
        if (storedVolumesString) {
            const storedVolumes = JSON.parse(storedVolumesString)
            setInstrumentVolumes(storedVolumes)
        }
    }, [])

    // Save volume values to storage whenever they change
    useEffect(() => {
        localStorage.setItem('instrumentVolumes', JSON.stringify(instrumentVolumes))
    }, [instrumentVolumes])

    return connectedInstruments && connectedInstruments.length > 0 ? (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
                height: isSmallScreen ? '80%' : '100%',
                gridAutoRows: isSmallScreen ? '10rem' : 'auto'
            }}
        >
            {connectedInstruments.map((instrument: Instrument, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', height: isSmallScreen ? '60%' : '150px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <VolumeIcon value={instrumentVolumes[instrument.deviceId]} />

                        <Slider
                            orientation="vertical"
                            aria-label="Volume"
                            value={instrumentVolumes[instrument.deviceId] ?? VOLUME_MARKS[1].value}
                            min={VOLUME_MIN}
                            max={VOLUME_MAX}
                            step={VOLUME_STEP}
                            marks={VOLUME_MARKS} // show the 5 ticks
                            valueLabelDisplay="off"
                            onChange={(e, v) => handleSliderChange(e, v, instrument.deviceId)}
                            onChangeCommitted={(e, v) => handleSliderCommit(e, v, instrument.deviceId)}
                            sx={{
                                height: isSmallScreen ? 100 : 120,
                                width: 10,
                                // thumb
                                '& .MuiSlider-thumb': {
                                    width: 14,
                                    height: 14,
                                    bgcolor: '#d5e6c3',
                                    border: '1px solid #3fad4471',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
                                },
                                // rail (full length) gradient
                                '& .MuiSlider-rail': {
                                    opacity: 1,
                                    background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                                    // "linear-gradient(to top, #e85e46 0%, #fee57b 30%, #d5e6c3 100%)",
                                    width: 6,
                                    borderRadius: 1
                                },
                                // track (filled portion) same gradient so it blends nicely
                                '& .MuiSlider-track': {
                                    background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                                    width: 6,
                                    borderRadius: 1,
                                    borderColor: '#d5e6c3'
                                },
                                // // marks (ticks)
                                '& .MuiSlider-mark': {
                                    width: 6,
                                    height: 3,
                                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                                    borderRadius: 1,
                                    left: '50%',
                                    transform: 'translateX(-50%)'
                                }
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '220px'
                        }}
                    >
                        <img src={handleImageSource(instrument.type, instrument.touchMode)} alt="Bild på instrument" height={isSmallScreen ? '100%' : '130px'} style={{ position: 'relative', zIndex: 1 }} />
                        <Box
                            onClick={() =>
                                toggleAllSongsDrawer({
                                    instrumentId: instrument.deviceId,
                                    instrumentType: instrument.type,
                                    instrumentSerialNr: instrument.serialNr
                                })
                            }
                            sx={{
                                borderRadius: '30%',
                                position: 'absolute',
                                bottom: isMobile ? 10 : 25,
                                right: isMobile ? 40 : 20,
                                zIndex: 2,
                                paddingRight: '4px',
                                '&:hover': { backgroundColor: 'rgb(227, 223, 209, 0.7)' }
                            }}
                        >
                            <MusicNoteIcon
                                sx={{
                                    color: 'rgba(0,0,0,0.8)',
                                    borderRadius: '50%',
                                    padding: '4px 0px 4px 4px',
                                    cursor: 'pointer',
                                    '&:hover': { color: 'black' }
                                }}
                            />
                        </Box>

                        <Drawer
                            variant="bottom"
                            isOpen={isAllSongsDrawerOpen}
                            toggleDrawer={() => toggleAllSongsDrawer({})}
                            // heading={polyglot.t("home.instrumentInformation.headingChooseSound")}
                        >
                            <AllSoundsAvailable songList={songList} selectedInstrument={selectedInstrument} hasChangedSoundCloseDrawer={hasChangedSoundCloseDrawer} />
                        </Drawer>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        {songList && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    height: isSmallScreen ? '100%' : 'auto'
                                }}
                            >
                                <InstrumentInformation instrument={instrument} songList={songList} shouldShowSettings={shouldShowSettings} isSmallScreen={isSmallScreen} currentSong={currentSong} />
                            </Box>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    ) : (
        <Box
            sx={{
                height: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isSmallScreen ? 'left' : 'center',
                paddingRight: isSmallScreen ? 0 : '10em'
            }}
        >
            <img src={Funkingen} alt="Funkingen" style={{ height: '100%' }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Text italic size={'heading'} textAlign="center">
                    {polyglot.t('home.instruments.headingWaiting')}
                </Text>
            </Box>
        </Box>
    )
}

export default Instruments
